import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box, Typography, Button, Paper, Grid, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import EditIcon         from "@mui/icons-material/Edit";
import SwapHorizIcon    from "@mui/icons-material/SwapHoriz";
import PersonIcon       from "@mui/icons-material/Person";
import BusinessIcon     from "@mui/icons-material/Business";
import ReceiptLongIcon  from "@mui/icons-material/ReceiptLong";
import api from "../../../services/api";

/* ─── Maps ─── */
const CONDITION_MAP = {
  baik:         { label: "Baik",         color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
  cukup_baik:   { label: "Cukup Baik",   color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
  rusak_ringan: { label: "Rusak Ringan",  color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  rusak_berat:  { label: "Rusak Berat",   color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
};
const STATUS_MAP = {
  aktif:           { label: "Aktif",           color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
  tidak_aktif:     { label: "Tidak Aktif",     color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" },
  dipinjam:        { label: "Dipinjam",        color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  dalam_perbaikan: { label: "Dalam Perbaikan", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
};
const URGENCY_MAP = {
  biasa:    { label: "Biasa",    color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" },
  segera:   { label: "Segera",   color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  mendesak: { label: "Mendesak", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
};
const TYPE_LABEL = {
  masuk:             "Masuk",
  pindah:            "Pindah",
  keluar:            "Keluar",
  pinjam:            "Dipinjam",
  kembali:           "Dikembalikan",
  perbaikan:         "Ke Perbaikan",
  selesai_perbaikan: "Selesai Perbaikan",
};
const TYPE_COLOR = {
  masuk:             "#16A34A",
  pindah:            "#2563EB",
  keluar:            "#DC2626",
  pinjam:            "#D97706",
  kembali:           "#16A34A",
  perbaikan:         "#D97706",
  selesai_perbaikan: "#16A34A",
};

/* ─── Helpers ─── */
const fmtDate = (val) => {
  if (!val) return "-";
  const s = typeof val === "string" && val.includes(" ") ? val.replace(" ", "T") : val;
  return new Date(s).toLocaleString("id-ID", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};
const fmtDateShort = (val) => {
  if (!val) return "-";
  return new Date(val).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtRupiah = (val) => val ? `Rp ${Number(val).toLocaleString("id-ID")}` : "-";

/* ─── Sub-components ─── */
function InfoRow({ label, value }) {
  return (
    <Box sx={{ py: 1.5, borderBottom: "1px solid #F1F5F9", display: "flex" }}>
      <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8", width: 170, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.875rem", color: "#0F172A", fontWeight: 500 }}>{value || "-"}</Typography>
    </Box>
  );
}

function StatusBadge({ map, value }) {
  const cfg = map[value] || { label: value, color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
  return (
    <Chip label={cfg.label} size="small" sx={{
      height: 22, fontSize: "0.68rem", fontWeight: 600,
      backgroundColor: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, borderRadius: "6px",
      "& .MuiChip-label": { px: "8px" },
    }} />
  );
}

function MovementTimeline({ movements }) {
  if (!movements?.length) return (
    <Box sx={{ py: 3, textAlign: "center" }}>
      <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8" }}>Belum ada riwayat perpindahan.</Typography>
    </Box>
  );
  return (
    <Box>
      {movements.map((mv, i) => (
        <Box key={mv.id} sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
            <Box sx={{
              width: 12, height: 12, borderRadius: "50%", mt: "4px", flexShrink: 0,
              backgroundColor: TYPE_COLOR[mv.type] || "#94A3B8",
              boxShadow: `0 0 0 3px ${(TYPE_COLOR[mv.type] || "#94A3B8")}33`,
            }} />
            {i < movements.length - 1 && (
              <Box sx={{ width: 2, flexGrow: 1, backgroundColor: "#E2E8F0", my: 0.5, minHeight: 24 }} />
            )}
          </Box>
          <Box sx={{ pb: i < movements.length - 1 ? 2 : 0, flex: 1 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#0F172A", mb: 0.25 }}>
              {TYPE_LABEL[mv.type] || mv.type}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#475569" }}>
              {mv.from_room ? `${mv.from_room.name} → ` : ""}{mv.to_room?.name || "-"}
            </Typography>
            {mv.note && (
              <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", fontStyle: "italic", mt: 0.2 }}>
                {mv.note}
              </Typography>
            )}
            <Typography sx={{ fontSize: "0.7rem", color: "#CBD5E1", mt: 0.4 }}>
              {mv.moved_at
                ? new Date(mv.moved_at.replace(" ", "T")).toLocaleString("id-ID", {
                    day: "numeric", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })
                : "-"}
              {mv.moved_by && ` · ${mv.moved_by.name}`}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ─── Procurement Info Section ─── */
function ProcurementSection({ procurementRequest, procurementItems }) {
  if (!procurementRequest) return null;

  const urgencyCfg = URGENCY_MAP[procurementRequest.urgency] || URGENCY_MAP.biasa;

  return (
    <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", p: 3, mt: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "#EFF6FF",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ReceiptLongIcon sx={{ fontSize: 16, color: "#2563EB" }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#0F172A" }}>
            Informasi Pengajuan
          </Typography>
        </Box>
        <Chip label={urgencyCfg.label} size="small" sx={{
          height: 22, fontSize: "0.68rem", fontWeight: 600,
          backgroundColor: urgencyCfg.bg, color: urgencyCfg.color,
          border: `1px solid ${urgencyCfg.border}`, borderRadius: "6px",
          "& .MuiChip-label": { px: "8px" },
        }} />
      </Box>

      {/* Pemohon & Departemen */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 1, borderRadius: "8px", backgroundColor: "#F8FAFC",
          border: "1px solid #E2E8F0", flex: "1 1 180px" }}>
          <PersonIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
          <Box>
            <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8", lineHeight: 1 }}>Nama Pemohon</Typography>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", mt: 0.25 }}>
              {procurementRequest.requester_name || "-"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 1, borderRadius: "8px", backgroundColor: "#F8FAFC",
          border: "1px solid #E2E8F0", flex: "1 1 180px" }}>
          <BusinessIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
          <Box>
            <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8", lineHeight: 1 }}>Departemen / Unit</Typography>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", mt: 0.25 }}>
              {procurementRequest.department || "-"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>Tanggal Pengajuan</Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "#0F172A", fontWeight: 500 }}>
            {fmtDateShort(procurementRequest.request_date || procurementRequest.created_at)}
          </Typography>
        </Box>
        {procurementRequest.purpose && (
          <Box>
            <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>Digunakan Untuk</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#0F172A", fontWeight: 500 }}>
              {procurementRequest.purpose}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Daftar Barang dari Pengajuan */}
      {procurementItems?.length > 0 && (
        <>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569", mb: 1 }}>
            Daftar Barang dalam Pengajuan
          </Typography>
          <TableContainer sx={{ borderRadius: "8px", border: "1px solid #E2E8F0", overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                  {["No", "Nama Barang", "Jumlah", "Harga Satuan", "Subtotal", "Keterangan"].map(h => (
                    <TableCell key={h} sx={{
                      fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8",
                      textTransform: "uppercase", letterSpacing: "0.04em",
                      py: "8px", px: "10px", borderBottom: "1px solid #E2E8F0",
                      whiteSpace: "nowrap",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {procurementItems.map((pi, i) => (
                  <TableRow key={pi.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ py: "8px", px: "10px", fontSize: "0.75rem", color: "#94A3B8" }}>
                      {i + 1}
                    </TableCell>
                    <TableCell sx={{ py: "8px", px: "10px", fontSize: "0.82rem",
                      fontWeight: 600, color: "#0F172A" }}>
                      {pi.item_name}
                    </TableCell>
                    <TableCell sx={{ py: "8px", px: "10px", fontSize: "0.82rem", color: "#475569" }}>
                      {pi.quantity}
                    </TableCell>
                    <TableCell sx={{ py: "8px", px: "10px", fontSize: "0.82rem", color: "#475569" }}>
                      {fmtRupiah(pi.price)}
                    </TableCell>
                    <TableCell sx={{ py: "8px", px: "10px", fontSize: "0.82rem",
                      fontWeight: 600, color: "#0F172A" }}>
                      {fmtRupiah(pi.subtotal)}
                    </TableCell>
                    <TableCell sx={{ py: "8px", px: "10px", fontSize: "0.78rem", color: "#94A3B8" }}>
                      {pi.notes || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}

/* ═══════════════════════════════════════════
   Main Component
═══════════════════════════════════════════ */
export default function ItemDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [item,       setItem]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [successMsg, setSuccessMsg] = useState(location.state?.message || "");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMsg(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    api.get(`/items/${id}`)
      .then((r) => setItem(r.data.data))
      .catch(() => setError("Gagal memuat data barang."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress sx={{ color: "#2563EB" }} />
    </Box>
  );
  if (error) return <Alert severity="error" sx={{ borderRadius: "8px" }}>{error}</Alert>;
  if (!item)  return null;

  // Data pengajuan dari relasi
  const procReq   = item.procurement_item?.procurement_request ?? null;
  // Semua item dalam pengajuan yang sama
  const procItems = procReq?.procurement_items ?? procReq?.items ?? null;

  return (
    <Box>
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2.5, borderRadius: "8px", fontWeight: 500 }} onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      )}

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items`)} startIcon={<ArrowBackIcon />} size="small"
            sx={{ textTransform: "none", color: "#475569",
              "&:hover": { backgroundColor: "#F1F5F9" } }}>
            Kembali
          </Button>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#0F172A" }}>{item.name}</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#94A3B8", fontFamily: "monospace" }}>
              {item.serial_number}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<SwapHorizIcon />}
            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${id}/move`)}
            sx={{ textTransform: "none", borderRadius: "8px",
              borderColor: "#E2E8F0", color: "#475569",
              "&:hover": { borderColor: "#2563EB", color: "#2563EB" } }}>
            Pindahkan
          </Button>
          <Button variant="contained" size="small" startIcon={<EditIcon />}
            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${id}/edit`)}
            sx={{ textTransform: "none", borderRadius: "8px",
              backgroundColor: "#2563EB", "&:hover": { backgroundColor: "#1D4ED8" } }}>
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {/* ── Kolom Kiri ── */}
        <Grid size={{ xs: 12, md: 7 }}>

          {/* Info Barang */}
          <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", p: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#0F172A", mb: 1.5 }}>
              Informasi Barang
            </Typography>

            <InfoRow label="Nama Barang"   value={item.name} />
            <InfoRow label="Serial Number" value={item.serial_number} />
            <InfoRow label="Kategori"      value={item.category} />
            <InfoRow label="Spesifikasi"   value={item.specification} />

            {/* Jumlah */}
            <Box sx={{ py: 1.5, borderBottom: "1px solid #F1F5F9", display: "flex" }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8", width: 170, flexShrink: 0 }}>Jumlah</Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "#0F172A", fontWeight: 700 }}>
                {item.quantity ?? 1} {item.unit || "Unit"}
              </Typography>
            </Box>

            <InfoRow label="Deskripsi" value={item.description} />

            {/* Kondisi */}
            <Box sx={{ py: 1.5, borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8", width: 170 }}>Kondisi</Typography>
              <StatusBadge map={CONDITION_MAP} value={item.condition} />
            </Box>

            {/* Status */}
            <Box sx={{ py: 1.5, borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#94A3B8", width: 170 }}>Status</Typography>
              <StatusBadge map={STATUS_MAP} value={item.status} />
            </Box>

            <InfoRow label="Tanggal Masuk" value={fmtDate(item.created_at)} />
            <InfoRow label="Tanggal Beli"  value={fmtDate(item.purchase_date)} />
            <InfoRow label="Harga Beli"    value={fmtRupiah(item.purchase_price)} />
            <InfoRow label="Ruangan"       value={item.room ? `${item.room.name} (${item.room.code})` : null} />
            <InfoRow label="Diinput oleh"  value={item.creator?.name} />
          </Paper>

          {/* Info Pengajuan (jika ada) */}
          <ProcurementSection
            procurementRequest={procReq}
            procurementItems={procItems}
          />
        </Grid>

        {/* ── Kolom Kanan: Riwayat Perpindahan ── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", p: 3, height: "100%" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#0F172A", mb: 2 }}>
              Riwayat Perpindahan
            </Typography>
            <MovementTimeline movements={item.movements} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
